import { defineComponent as _defineComponent } from 'vue'
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { getDemoUrl } from '@@/bits/url'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPadletLogo, { OzPadletLogoSizePreset } from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import { getAnchorFromElement } from '@@/library/v4/components/OzPopoverModal.vue'
import OzTabsWithSeparator, {
  OzTabsWithSeparatorAccessibilityRole,
  OzTabsWithSeparatorSizePreset,
} from '@@/library/v4/components/OzTabsWithSeparator.vue'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { LandsidePage, useLandsideNavigationStore } from '@@/pinia/landside_navigation'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { storeToRefs } from 'pinia'
import { useLandsideQuoteModalStore } from '@@/pinia/landside_quote_modal'
import { computed, defineAsyncComponent, ref } from 'vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'

export default /*#__PURE__*/_defineComponent({
  __name: 'LandsideHeader',
  setup(__props) {

const UserMenu = defineAsyncComponent(() => import('@@/vuecomponents/UserMenu.vue'))

const LandsideNavigationSidePanel = defineAsyncComponent(
  () => import('@@/vuecomponents/LandsideNavigationSidePanel.vue'),
)
const LandsideNavigationPopover = defineAsyncComponent(() => import('@@/vuecomponents/LandsideNavigationPopover.vue'))

const landsideNavigationStore = useLandsideNavigationStore()
const appBarAccountStore = useAppBarAccountStore()
const {
  isSmallerThanTabletPortrait,
  isSmallerThanDesktop3XL,
  isSmallerThanDesktop,
  isBiggerThanDesktopBig,
  isBiggerThanDesktop,
} = storeToRefs(useWindowSizeStore())
const { openQuoteModal } = useLandsideQuoteModalStore()

const { currentPage, landsidePages, currentUser } = storeToRefs(landsideNavigationStore)
const { xUserMenu } = storeToRefs(appBarAccountStore)
const { showUserMenu } = appBarAccountStore

const xSidePanel = ref(false)
const xPopoverMenu = ref(false)
const menuAnchor = ref<Partial<PopoverAnchor>>({})
const navigationMenuButtonRef = ref<InstanceType<typeof OzIconButton>>()

const demoLink = computed(() =>
  getDemoUrl('external', { pageName: currentPageTitle.value?.toLowerCase(), linkLocation: 'top_bar' }),
)

const tabs = computed(() => {
  return (
    landsidePages.value
      .filter((page) => page.key !== LandsidePage.Home)
      // remove the "What's new" tab at smaller desktops to prevent cramping
      .filter(
        (page) => page.key !== LandsidePage.WhatsNew || isSmallerThanDesktop.value || isBiggerThanDesktopBig.value,
      )
  )
})

const currentPageTitle = computed(() => {
  const page = landsidePages.value.find((page) => page.key === currentPage.value)
  return page?.text
})

const xPageTitle = computed(() => {
  if (!isSmallerThanDesktop.value) return false
  return currentPage.value !== LandsidePage.Home
})

function openNavigationMenu(): void {
  if (isSmallerThanTabletPortrait.value) {
    xSidePanel.value = true
  } else if (isSmallerThanDesktop.value) {
    xPopoverMenu.value = true
    updateActionMenuAnchor()
  }
}

function updateActionMenuAnchor() {
  const menuButton = navigationMenuButtonRef.value?.$el
  if (!menuButton) return

  const anchor = getAnchorFromElement(menuButton, { top: 2, left: -12.25 })
  menuAnchor.value = {
    ...anchor,
    position: 'side',
  }
}

function handleSelectPage(page: LandsidePage) {
  if ([LandsidePage.Pricing, LandsidePage.Subscriptions].includes(page)) {
    const excludedButtonTitle = localStorage.getItem('excludedButtonTitle')
    if (excludedButtonTitle === LandsidePage.Pricing) {
      trackEvent('Landside', 'Pricing/Subscriptions Clicks', 'Pricing')
    } else if (excludedButtonTitle === LandsidePage.Subscriptions) {
      trackEvent('Landside', 'Pricing/Subscriptions Clicks', 'Subscriptions')
    }
  }

  if ([LandsidePage.Help, LandsidePage.Careers, LandsidePage.WhatsNew].includes(page)) {
    landsideNavigationStore.setCurrentPage(page)
  } else {
    landsideNavigationStore.setCurrentPageWithSideEffects(page)
  }
}

function handleActivateTab(_e, { key }: { key: LandsidePage }) {
  if ([LandsidePage.Pricing, LandsidePage.Subscriptions].includes(key)) {
    const excludedButtonTitle = localStorage.getItem('excludedButtonTitle')
    if (excludedButtonTitle === LandsidePage.Pricing) {
      trackEvent('Landside', 'Pricing Button Clicked')
    } else if (excludedButtonTitle === LandsidePage.Subscriptions) {
      trackEvent('Landside', 'Subscriptions Button Clicked')
    }
  }

  if ([LandsidePage.Help, LandsidePage.Careers, LandsidePage.WhatsNew].includes(key)) {
    landsideNavigationStore.setCurrentPage(key)
  } else {
    landsideNavigationStore.setCurrentPageWithSideEffects(key)
  }
}

const isHomePage = computed((): boolean => {
  return currentPage.value === LandsidePage.Home
})

const isTemplatesPage = computed((): boolean => {
  return currentPage.value === LandsidePage.Templates
})

const isSubscriptionsPage = computed((): boolean => {
  return currentPage.value === LandsidePage.Subscriptions
})

const avatarSize = computed((): number => {
  if (isSmallerThanTabletPortrait.value) return 20
  if (isSmallerThanDesktop3XL.value) return 24
  return 40
})

const avatarButtonSize = computed((): number => {
  if (isSmallerThanTabletPortrait.value) return 32
  if (isSmallerThanDesktop3XL.value) return 40
  return 56
})

const avatarButtonDistanceFromEnd = computed(() => {
  const SCROLLBAR_WIDTH = 14

  if (isSmallerThanTabletPortrait.value) return 12 + SCROLLBAR_WIDTH
  if (isSmallerThanDesktop3XL.value) return 20 + SCROLLBAR_WIDTH
  return 32 + SCROLLBAR_WIDTH
})

const avatarButtonDistanceFromTop = computed(() => {
  if (isSmallerThanTabletPortrait.value) return 8
  if (isSmallerThanDesktop3XL.value) return 16
  return 0
})

const HEADER_BACKGROUND_COLOR_CLASS = 'bg-grey-700 dark:bg-grey-800'

return { __sfc: true,UserMenu, LandsideNavigationSidePanel, LandsideNavigationPopover, landsideNavigationStore, appBarAccountStore, isSmallerThanTabletPortrait, isSmallerThanDesktop3XL, isSmallerThanDesktop, isBiggerThanDesktopBig, isBiggerThanDesktop, openQuoteModal, currentPage, landsidePages, currentUser, xUserMenu, showUserMenu, xSidePanel, xPopoverMenu, menuAnchor, navigationMenuButtonRef, demoLink, tabs, currentPageTitle, xPageTitle, openNavigationMenu, updateActionMenuAnchor, handleSelectPage, handleActivateTab, isHomePage, isTemplatesPage, isSubscriptionsPage, avatarSize, avatarButtonSize, avatarButtonDistanceFromEnd, avatarButtonDistanceFromTop, HEADER_BACKGROUND_COLOR_CLASS, __, OzAvatar, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, OzIconButton, OzIconButtonColorScheme, OzIconButtonSizePreset, OzPadletLogo, OzPadletLogoSizePreset, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, OzTabsWithSeparator, OzTabsWithSeparatorAccessibilityRole, OzTabsWithSeparatorSizePreset, LandsidePage, SkipToContentLink }
}

})